import { Error } from '@mui/icons-material'
import { Chip } from '@mui/material'

type StatusLabel = {
  status: string
  size?: 'small' | 'medium'
  statusType:
    | typeof VehicleJobStatus
    | typeof BookingStatus
    | typeof VehicleJobProgress
    | typeof ActiveStatus
    | string
}

export enum VehicleJobStatus {
  Unallocated = 'Unallocated',
  Awaiting_Dispatch = 'Awaiting Dispatch',
  Not_Dispatched = 'Not Dispatched',
  Ontime = 'Ontime',
  Delayed = 'Delayed',
  Driver_Waiting = 'Driver Waiting',
  In_Progress = 'In Progress',
  Attention_Needed = 'Attention Needed',
}

export enum VehicleJobProgress {
  Allocated = 'Allocated',
  Declined = 'Declined',
  Confirmed = 'Confirmed',
  Cancelled = 'Cancelled',
  OnRoute = 'OnRoute',
  Arrived = 'Arrived',
  Passenger_Picked_Up = 'Passenger Picked Up',
  Passenger_Dropped_Off = 'Passenger Dropped Off',
  OnRoute_Passenger_On_Board = 'OnRoute Passenger On Board',
  Completed = 'Completed',
  Supplier_Cancelled = 'Supplier Cancelled',
  No_Show = 'No Show',
  Driver_Allocated = 'Driver Allocated',
  Manual_Reallocation_Pending = 'Manual Reallocation Pending',
  Manual_Intervention_Required = 'Manual Intervention Required',
  Completed_Pending_Invoice = 'Completed (Pending Invoice)',
  Booking_Update_Pending = 'Update Pending',
}

export enum BookingStatus {
  Quoted = 'Quoted',
  Confirmed = 'Confirmed',
  Cancelled = 'Cancelled',
  InProgress = 'In Progress',
  In_Progress = 'In Progress',
  Complete = 'Complete',
}

export enum ActiveStatus {
  Active = 'Active',
  Deleted = 'Deleted',
}

function getStatusKey(type: string, status: string) {
  return `${type}:${status}`
}

const statusRagMap: Record<
  string,
  'red' | 'amber' | 'green' | 'neutral' | 'urgent'
> = {
  // VehicleJobStatus
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Unallocated)]: 'amber',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Awaiting_Dispatch)]:
    'green',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Not_Dispatched)]: 'red',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Ontime)]: 'green',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Delayed)]: 'red',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Driver_Waiting)]: 'amber',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.In_Progress)]: 'neutral',
  [getStatusKey('VehicleJobStatus', VehicleJobStatus.Attention_Needed)]:
    'urgent',
  [getStatusKey(
    'VehicleJobProgress',
    VehicleJobProgress.Completed_Pending_Invoice,
  )]: 'green',

  // VehicleJobProgress
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Allocated)]: 'neutral',
  [getStatusKey(
    'VehicleJobProgress',
    VehicleJobProgress.Booking_Update_Pending,
  )]: 'neutral',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Declined)]: 'red',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Confirmed)]: 'green',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Cancelled)]: 'red',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.OnRoute)]: 'green',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Arrived)]: 'neutral',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Passenger_Picked_Up)]:
    'green',
  [getStatusKey(
    'VehicleJobProgress',
    VehicleJobProgress.Passenger_Dropped_Off,
  )]: 'green',
  [getStatusKey(
    'VehicleJobProgress',
    VehicleJobProgress.OnRoute_Passenger_On_Board,
  )]: 'green',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Completed)]: 'green',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Supplier_Cancelled)]:
    'red',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.No_Show)]: 'red',
  [getStatusKey('VehicleJobProgress', VehicleJobProgress.Driver_Allocated)]:
    'neutral',
  [getStatusKey(
    'VehicleJobProgress',
    VehicleJobProgress.Manual_Reallocation_Pending,
  )]: 'amber',
  [getStatusKey(
    'VehicleJobProgress',
    VehicleJobProgress.Manual_Intervention_Required,
  )]: 'red',

  [getStatusKey('BookingStatus', BookingStatus.Quoted)]: 'neutral',
  [getStatusKey('BookingStatus', BookingStatus.Confirmed)]: 'green',
  [getStatusKey('BookingStatus', BookingStatus.Cancelled)]: 'red',
  [getStatusKey('BookingStatus', BookingStatus.InProgress)]: 'green',
  [getStatusKey('BookingStatus', BookingStatus.In_Progress)]: 'green',
  [getStatusKey('BookingStatus', BookingStatus.Complete)]: 'green',

  // ActiveStatus
  [getStatusKey('ActiveStatus', ActiveStatus.Active)]: 'green',
  [getStatusKey('ActiveStatus', ActiveStatus.Deleted)]: 'red',
}

export default function StatusChip({
  status,
  statusType,
  size = 'small',
}: StatusLabel) {
  const typeNameMap = new Map<string, any>([
    ['VehicleJobStatus', VehicleJobStatus],
    ['VehicleJobProgress', VehicleJobProgress],
    ['BookingStatus', BookingStatus],
    ['ActiveStatus', ActiveStatus],
  ])
  const typeName = typeNameMap.get(statusType as string)
  // console.log('rag typename', typeName)

  // Find the label from the enum object
  const enumEntries =
    typeName && typeof typeName === 'object' ? Object.entries(typeName) : []
  // console.log('rag enumEntries', enumEntries)
  const foundEntry = enumEntries.find(([key]) => key === status)
  const label = foundEntry ? foundEntry[1] : status

  const statusKey = foundEntry
    ? getStatusKey(statusType as string, foundEntry[1] as string)
    : getStatusKey(statusType as string, status as string)

  const rag = statusRagMap[statusKey]

  const color = ['red', 'urgent'].includes(rag)
    ? 'error'
    : rag === 'amber'
    ? 'warning'
    : rag === 'green'
    ? 'success'
    : rag === 'neutral'
    ? 'primary'
    : 'info'

  return status ? (
    <Chip
      label={label}
      color={color}
      size={size}
      sx={size === 'small' ? { fontSize: '12px' } : {}}
      icon={rag === 'urgent' ? <Error /> : <></>}
    />
  ) : (
    <></>
  )
}
